export type DocumentControlReviewState =
  | 'draft'
  | 'for-review'
  | 'reviewed'
  | 'for-approval'
  | 'approved'
  | 'rejected'
  | 'superseded';

export interface DocumentControlMetadataInput {
  documentNumber?: string | null;
  title?: string | null;
  revisionCode?: string | null;
  statusCode?: string | null;
  category?: string | null;
  discipline?: string | null;
  originator?: string | null;
  zone?: string | null;
  level?: string | null;
  packageName?: string | null;
  issueDate?: string | null;
  reviewState?: DocumentControlReviewState | string | null;
  confidentiality?: string | null;
  documentFamilyKey?: string | null;
  quantisDocumentId?: string | null;
  projectId?: string | null;
  clientId?: string | null;
  folderKey?: string | null;
  currentRevisionCode?: string | null;
  supersedesDriveFileId?: string | null;
  supersededByDriveFileId?: string | null;
  uploadCommandId?: string | null;
  revisionPending?: boolean;
  /** Upload transport CAS; never written into provider appProperties. */
  expectedPreviousMetadataRevision?: string | null;
}

export const DRIVE_APP_PROPERTY_KEYS = {
  documentNumber: 'documentNumber',
  revisionCode: 'revisionCode',
  statusCode: 'statusCode',
  category: 'category',
  discipline: 'discipline',
  originator: 'originator',
  zone: 'zone',
  level: 'level',
  packageName: 'packageName',
  issueDate: 'issueDate',
  reviewState: 'reviewState',
  confidentiality: 'confidentiality',
  documentFamilyKey: 'documentFamilyKey',
  quantisDocumentId: 'quantisDocumentId',
  projectId: 'projectId',
  clientId: 'clientId',
  folderKey: 'folderKey',
  currentRevisionCode: 'currentRevisionCode',
  supersedesDriveFileId: 'supersedesDriveFileId',
  supersededByDriveFileId: 'supersededByDriveFileId',
  uploadCommandId: 'quantisUploadCommandId',
} as const;

function cleanString(value?: string | null) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized.length > 0 ? normalized : undefined;
}

export function normalizeDocumentControlMetadata(
  input?: Partial<DocumentControlMetadataInput> | null,
): DocumentControlMetadataInput | undefined {
  if (!input) return undefined;
  const normalized: DocumentControlMetadataInput = {
    documentNumber: cleanString(input.documentNumber),
    title: cleanString(input.title),
    revisionCode: cleanString(input.revisionCode),
    statusCode: cleanString(input.statusCode),
    category: cleanString(input.category),
    discipline: cleanString(input.discipline),
    originator: cleanString(input.originator),
    zone: cleanString(input.zone),
    level: cleanString(input.level),
    packageName: cleanString(input.packageName),
    issueDate: cleanString(input.issueDate),
    reviewState: cleanString(input.reviewState),
    confidentiality: cleanString(input.confidentiality),
    documentFamilyKey: cleanString(input.documentFamilyKey),
    quantisDocumentId: cleanString(input.quantisDocumentId),
    projectId: cleanString(input.projectId),
    clientId: cleanString(input.clientId),
    folderKey: cleanString(input.folderKey),
    currentRevisionCode: cleanString(input.currentRevisionCode),
    supersedesDriveFileId: cleanString(input.supersedesDriveFileId),
    supersededByDriveFileId: cleanString(input.supersededByDriveFileId),
    uploadCommandId: cleanString(input.uploadCommandId),
    revisionPending: input.revisionPending === true ? true : undefined,
    expectedPreviousMetadataRevision: cleanString(input.expectedPreviousMetadataRevision),
  };

  const hasValue = Object.values(normalized).some(Boolean);
  return hasValue ? normalized : undefined;
}

export function toDriveAppProperties(
  metadata?: Partial<DocumentControlMetadataInput> | null,
): Record<string, string> | undefined {
  const normalized = normalizeDocumentControlMetadata(metadata);
  if (!normalized) return undefined;

  const appPropertiesEntries = Object.entries({
    [DRIVE_APP_PROPERTY_KEYS.documentNumber]: normalized.documentNumber,
    [DRIVE_APP_PROPERTY_KEYS.revisionCode]: normalized.revisionCode,
    [DRIVE_APP_PROPERTY_KEYS.statusCode]: normalized.statusCode,
    [DRIVE_APP_PROPERTY_KEYS.category]: normalized.category,
    [DRIVE_APP_PROPERTY_KEYS.discipline]: normalized.discipline,
    [DRIVE_APP_PROPERTY_KEYS.originator]: normalized.originator,
    [DRIVE_APP_PROPERTY_KEYS.zone]: normalized.zone,
    [DRIVE_APP_PROPERTY_KEYS.level]: normalized.level,
    [DRIVE_APP_PROPERTY_KEYS.packageName]: normalized.packageName,
    [DRIVE_APP_PROPERTY_KEYS.issueDate]: normalized.issueDate,
    [DRIVE_APP_PROPERTY_KEYS.reviewState]: normalized.reviewState,
    [DRIVE_APP_PROPERTY_KEYS.confidentiality]: normalized.confidentiality,
    [DRIVE_APP_PROPERTY_KEYS.documentFamilyKey]: normalized.documentFamilyKey,
    [DRIVE_APP_PROPERTY_KEYS.quantisDocumentId]: normalized.quantisDocumentId,
    [DRIVE_APP_PROPERTY_KEYS.projectId]: normalized.projectId,
    [DRIVE_APP_PROPERTY_KEYS.clientId]: normalized.clientId,
    [DRIVE_APP_PROPERTY_KEYS.folderKey]: normalized.folderKey,
    [DRIVE_APP_PROPERTY_KEYS.currentRevisionCode]: normalized.currentRevisionCode,
    [DRIVE_APP_PROPERTY_KEYS.supersedesDriveFileId]: normalized.supersedesDriveFileId,
    [DRIVE_APP_PROPERTY_KEYS.supersededByDriveFileId]: normalized.supersededByDriveFileId,
    [DRIVE_APP_PROPERTY_KEYS.uploadCommandId]: normalized.uploadCommandId,
  }).filter(([, value]) => typeof value === 'string' && value.length > 0);

  return appPropertiesEntries.length > 0
    ? Object.fromEntries(appPropertiesEntries as Array<[string, string]>)
    : undefined;
}

function slugPart(value?: string | null) {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildDocumentFamilyKey(input: {
  projectId?: string | null;
  documentNumber?: string | null;
  title?: string | null;
}) {
  const parts = [
    slugPart(input.projectId),
    slugPart(input.documentNumber) || slugPart(input.title) || 'document',
  ].filter(Boolean);

  return parts.join('--') || undefined;
}

/** Drive uses explicit nulls to remove app properties; identity keys never clear through detail editors. */
export const EDITABLE_DOCUMENT_METADATA_KEYS = ['documentNumber', 'revisionCode', 'statusCode', 'category', 'discipline', 'originator', 'zone', 'level', 'packageName', 'issueDate', 'reviewState', 'confidentiality'] as const;
/** Native storage persists title in metadata; Drive's title remains its separate file name. */
export const NATIVE_EDITABLE_DOCUMENT_METADATA_KEYS = ['title', ...EDITABLE_DOCUMENT_METADATA_KEYS] as const;
export function buildDocumentAppPropertyUpdate(metadata?: DocumentControlMetadataInput, clearFields: string[] = []): Record<string, string | null> {
  const properties: Record<string, string | null> = { ...toDriveAppProperties(metadata) };
  for (const key of clearFields) {
    if (!(EDITABLE_DOCUMENT_METADATA_KEYS as readonly string[]).includes(key)) throw new Error('Document identity cannot be cleared');
    properties[key] = null;
  }
  return properties;
}
